'use client';

import type { EmblaCarouselType } from 'embla-carousel-react';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

// Define the context type
export type CarouselContext = {
  api: EmblaCarouselType | null;
  setApi: (api: EmblaCarouselType | null) => void;
  canGoNext: boolean;
  setCanGoNext: (canGoNext: boolean) => void;
  canGoPrev: boolean;
  setCanGoPrev: (canGoPrev: boolean) => void;
  selectedIndex: number;
  setSelectedIndex: (selectedIndex: number) => void;
  slideCount: number;
  setSlideCount: (slideCount: number) => void;
};

const CarouselContext = createContext<CarouselContext | undefined>(undefined);

export const CarouselProvider = ({ children }: PropsWithChildren) => {
  const [api, setApi] = useState<EmblaCarouselType | null>(null);
  const [canGoNext, setCanGoNext] = useState(false);
  const [canGoPrev, setCanGoPrev] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [slideCount, setSlideCount] = useState(0);

  const value = useMemo(
    () => ({
      api,
      setApi,
      canGoNext,
      setCanGoNext,
      canGoPrev,
      setCanGoPrev,
      selectedIndex,
      setSelectedIndex,
      slideCount,
      setSlideCount,
    }),
    [api, canGoNext, canGoPrev, selectedIndex, slideCount],
  );

  return <CarouselContext.Provider value={value}>{children}</CarouselContext.Provider>;
};

export function useCarouselContext(): CarouselContext {
  const context = useContext(CarouselContext);
  if (context === undefined) {
    throw new Error('useCarouselContext must be used within a CarouselProvider');
  }
  return context;
}
